import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { StatusChip, BotAvatar, CapabilityChip, EmptyState, ErrorPanel } from "@/components/shared";
import WelcomeFlowTab from "@/pages/journeys/WelcomeFlowTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ChevronLeft, ExternalLink, Send, MoreHorizontal, KeyRound, Trash2, Loader2, Plus, GripVertical,
  ShieldCheck, RefreshCw, CheckCircle2, AlertCircle, ImagePlus, ImageOff, Lock, Play, Square, RotateCw, Server,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const BASE_TABS = ["Overview", "Welcome Flow", "Profile", "Commands", "Settings", "Messages", "Users", "Webhook", "Automations", "Analytics", "Logs", "Security", "Capabilities"];
// Access settings are a Managed Bots feature; the tab appears only where the
// adapter can actually reach Telegram.
const tabsFor = (bot) =>
  bot?.management_status === "FULL_MANAGED"
    ? [...BASE_TABS.slice(0, 12), "Access", ...BASE_TABS.slice(12)]
    : BASE_TABS;

const Card = ({ children, className = "" }) => <div className={`bg-card border border-border rounded-lg p-5 ${className}`}>{children}</div>;
const Row = ({ k, v }) => <div className="flex items-center justify-between py-2 border-b border-border/50 text-sm"><span className="text-muted-foreground">{k}</span><span className="mono">{v}</span></div>;

/* ---------------- OVERVIEW ---------------- */
function Overview({ bot }) {
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/analytics`).then((r) => setAnalytics(r.data)).catch(() => {}); }, [bot.id]);
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2">
        <h3 className="font-semibold mb-3">Activity · last 7 days</h3>
        {analytics ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={analytics.series}>
              <defs><linearGradient id="oIn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#229ED9" stopOpacity={0.4} /><stop offset="100%" stopColor="#229ED9" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="incoming" stroke="#229ED9" fill="url(#oIn)" strokeWidth={2} />
              <Area type="monotone" dataKey="outgoing" stroke="#10b981" fill="transparent" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        ) : <Skeleton className="h-52 w-full" />}
      </Card>
      <Card>
        <h3 className="font-semibold mb-3">Details</h3>
        <Row k="Bot ID" v={bot.telegram_bot_id || "—"} />
        <Row k="Username" v={`@${bot.username || "—"}`} />
        <Row k="Source" v={bot.origin || bot.source} />
        <Row k="Token" v={bot.token_fingerprint ? bot.token_fingerprint.slice(0, 18) + "…" : "—"} />
        <Row k="Rotated" v={bot.last_rotated_at ? new Date(bot.last_rotated_at).toLocaleDateString() : "—"} />
        <div className="mt-3 space-y-1.5">
          {(bot.health?.reasons || []).map((r, i) => (
            <div key={i} className="text-xs flex items-start gap-1.5 text-muted-foreground">
              {bot.health.status === "HEALTHY" ? <CheckCircle2 size={13} className="text-emerald-500 mt-0.5" /> : <AlertCircle size={13} className="text-amber-500 mt-0.5" />} {r}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------------- PROFILE PHOTO (avatar — Bot API 9.4: setMyProfilePhoto) ----------------
   The avatar beside the bot's name (chat list, chat header, profile). This is NOT the
   intro / description picture on the empty-chat screen — that lives in <IntroMedia>. */
function ProfilePhoto({ bot, name }) {
  const [busy, setBusy] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(!!bot.has_profile_photo);
  const [pending, setPending] = useState(null); // { file, url, animated } — picked, previewed, not yet uploaded
  const inputRef = React.useRef(null);

  useEffect(() => () => { if (pending?.url) URL.revokeObjectURL(pending.url); }, [pending]);

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPending({ file, url: URL.createObjectURL(file), animated: file.type.startsWith("video/") });
  };

  const upload = async () => {
    if (!pending) return;
    const form = new FormData();
    form.append("file", pending.file);
    form.append("animated", pending.animated ? "true" : "false");
    setBusy(true);
    try {
      await api.post(`/bots/${bot.id}/profile/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setHasPhoto(true);
      setPending(null);
      toast.success("Profile photo updated", { description: "Confirmed by Telegram." });
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/bots/${bot.id}/profile/photo`);
      setHasPhoto(false);
      toast.success("Profile photo removed");
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };

  return (
    <div className="border-t border-border pt-4 space-y-3" data-testid="section-profile-photo">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="mb-0 text-xs uppercase tracking-wide text-muted-foreground">Profile Photo</Label>
        <CapabilityChip chip="Direct" />
        <span className="text-[11px] text-muted-foreground mono">setMyProfilePhoto</span>
      </div>
      <p className="text-xs text-muted-foreground">
        The avatar shown beside the bot's name in the chat list and chat header. Set directly through the Bot API — a JPG, or an MP4 for an animated photo.
        It is <span className="font-medium text-foreground">not</span> the intro picture shown on the empty-chat screen (see Description &amp; Intro below).
      </p>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0" data-testid="profile-photo-preview">
          {pending ? (
            pending.animated
              ? <video src={pending.url} className="w-full h-full object-cover" muted autoPlay loop playsInline />
              : <img src={pending.url} alt="Profile photo preview" className="w-full h-full object-cover" />
          ) : <BotAvatar name={name || bot.name} username={bot.username} size={64} />}
        </div>
        <div className="text-xs text-muted-foreground min-w-0">
          {pending
            ? <>Preview of <span className="mono text-foreground break-all">{pending.file.name}</span> — not uploaded yet.</>
            : hasPhoto ? "A profile photo is set on Telegram." : "No profile photo set — Telegram shows the initial letter."}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/jpg,image/png,video/mp4" className="hidden" onChange={pick} data-testid="input-profile-photo" />
      <div className="flex items-center gap-2 flex-wrap">
        {pending ? (
          <>
            <Button size="sm" disabled={busy} onClick={upload} className="gap-1.5" data-testid="btn-confirm-profile-photo">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {hasPhoto ? "Replace photo" : "Upload photo"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPending(null)} data-testid="btn-cancel-profile-photo">Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="gap-1.5" data-testid="btn-upload-profile-photo">
              <ImagePlus size={14} /> {hasPhoto ? "Replace photo" : "Upload photo"}
            </Button>
            {hasPhoto && (
              <Button size="sm" variant="ghost" className="text-rose-500" disabled={busy} onClick={remove} data-testid="btn-remove-profile-photo">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} Remove
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- INTRO MEDIA VIEW (shared by the section frame and the empty-chat preview) ---------------- */
const introMediaUrls = (bot, media) => {
  if (!media) return { fileUrl: null, thumbUrl: null };
  const v = encodeURIComponent(media.fetched_at || "");
  const base = `${api.defaults.baseURL}/bots/${bot.id}/intro/media`;
  return {
    fileUrl: media.has_file ? `${base}?v=${v}` : null,
    thumbUrl: media.has_thumb ? `${base}?thumb=1&v=${v}` : null,
  };
};

function IntroMediaView({ bot, intro, loading, compact = false }) {
  if (loading) return <Skeleton className={compact ? "h-32 w-full rounded-none" : "h-40 w-full"} />;
  const media = intro?.media;
  const muted = `flex flex-col items-center justify-center gap-1.5 text-center text-xs text-muted-foreground ${compact ? "py-6 px-3" : "py-10 px-4"}`;
  if (!media) {
    return (
      <div className={muted} data-testid="intro-media-unknown">
        <AlertCircle size={18} className="text-amber-500" />
        <span>Could not read the intro media from Telegram.</span>
        {!compact && intro?.reason ? <span className="max-w-xs">{intro.reason}</span> : null}
      </div>
    );
  }
  if (media.kind === "none") {
    return (
      <div className={muted} data-testid="intro-media-none">
        <ImageOff size={18} />
        <span>No description media set — Telegram shows only the text.</span>
      </div>
    );
  }
  const { fileUrl, thumbUrl } = introMediaUrls(bot, media);
  const frame = compact ? "w-full max-h-48 object-contain bg-muted" : "w-full max-h-64 object-contain";
  if (media.kind === "photo" && fileUrl) return <img src={fileUrl} alt="Description media" className={frame} data-testid="intro-media-photo" />;
  if ((media.kind === "animation" || media.kind === "video") && fileUrl) {
    return (
      <video src={fileUrl} poster={thumbUrl || undefined} className={frame} controls={media.kind === "video"}
             autoPlay={media.kind === "animation"} loop muted playsInline data-testid={`intro-media-${media.kind}`} />
    );
  }
  if (thumbUrl) {
    return (
      <div className="relative w-full">
        <img src={thumbUrl} alt="Description media thumbnail" className={frame} data-testid="intro-media-thumb" />
        <div className="absolute bottom-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded bg-background/80 text-foreground border border-border">
          {media.kind} · thumbnail only{media.size ? ` (${(media.size / 1048576).toFixed(1)} MB)` : ""}
        </div>
      </div>
    );
  }
  return (
    <div className={muted} data-testid="intro-media-nopreview">
      <ImageOff size={18} />
      <span>{media.kind} is set on Telegram, but no preview could be downloaded.</span>
    </div>
  );
}

/* ---------------- DESCRIPTION MEDIA (intro picture — read via MTProto, write is BotFather-only) ---------------- */
function IntroMedia({ bot, intro, loading, refreshing, onRefresh }) {
  const media = intro?.media;
  const writable = !!intro?.media_supported?.write;
  const how = intro?.write_how || "@BotFather → /setdescriptionpic → choose bot → send image/GIF";

  const botfather = (action) => {
    // No Bot API / MTProto write path exists: hand the user to BotFather instead of pretending.
    toast.info(`${action} the description media in @BotFather`, { description: how });
    window.open("https://t.me/BotFather", "_blank", "noopener");
  };

  const meta = media && media.kind !== "none"
    ? [media.kind, media.width && media.height ? `${media.width}×${media.height}` : null,
       media.duration ? `${Math.round(media.duration)}s` : null, media.stale ? "stale copy" : null].filter(Boolean).join(" · ")
    : null;

  return (
    <div className="border-t border-border pt-4 space-y-3" data-testid="section-intro-media">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="mb-0">Description media</Label>
        <CapabilityChip chip="BotFather" />
        <span className="text-[11px] text-muted-foreground mono">/setdescriptionpic · read live via MTProto</span>
      </div>
      <p className="text-xs text-muted-foreground">
        The picture, GIF or video shown above the description on the empty-chat screen, before the user presses START.
        Separate from the profile photo above — Telegram stores and sets them independently.
      </p>

      <div className="rounded-lg border border-border bg-muted/40 overflow-hidden" data-testid="intro-media-frame">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border flex items-center justify-between gap-2">
          <span>Intro media preview</span>
          {meta ? <span className="mono normal-case" data-testid="intro-media-meta">{meta}</span> : null}
        </div>
        <div className="flex items-center justify-center bg-background min-h-[8rem]">
          <IntroMediaView bot={bot} intro={intro} loading={loading} />
        </div>
      </div>

      {intro?.reason && !loading ? (
        <div className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5" data-testid="intro-media-reason">
          <AlertCircle size={13} className="mt-0.5 shrink-0" /> <span>{intro.reason}</span>
        </div>
      ) : null}

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="gap-1.5" disabled={loading}
                onClick={() => (writable ? null : botfather("Replace"))} data-testid="btn-replace-intro-media">
          {writable ? <ImagePlus size={14} /> : <Lock size={13} />} Replace media
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5 text-rose-500" disabled={loading || !media || media.kind === "none"}
                onClick={() => (writable ? null : botfather("Remove"))} data-testid="btn-remove-intro-media">
          {writable ? null : <Lock size={13} />} Remove
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5" disabled={loading || refreshing} onClick={onRefresh} data-testid="btn-refresh-intro">
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh from Telegram
        </Button>
      </div>

      {!writable && (
        <div className="rounded-md border border-blue-600/20 bg-blue-600/10 p-3 text-xs space-y-1.5" data-testid="intro-media-botfather">
          <div className="font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
            <ExternalLink size={13} /> BotFather / Telegram action required
          </div>
          <div className="text-muted-foreground">
            Telegram exposes no API to set or remove the description media (Bot API {" "}
            <span className="mono">setMyDescription</span> is text-only; <span className="mono">setMyProfilePhoto</span> is the avatar). BotControl reads it live but can only change it through @BotFather:
          </div>
          <div className="mono text-foreground/90">{how}</div>
          <div className="text-muted-foreground">To remove it, run <span className="mono">/setdescriptionpic</span> again and send <span className="mono">/empty</span>. Then press “Refresh from Telegram” here to pull the new state.</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- LIVE PREVIEWS ---------------- */
function ProfilePreview({ bot, name, short }) {
  return (
    <div className="rounded-xl border border-border bg-background max-w-sm shadow-sm overflow-hidden" data-testid="preview-profile">
      <div className="bg-primary/10 px-4 pt-6 pb-4 flex flex-col items-center text-center">
        <div className="rounded-full overflow-hidden mb-3 ring-4 ring-background shadow"><BotAvatar name={name || bot.name} username={bot.username} size={72} /></div>
        <div className="font-semibold text-foreground text-base leading-tight">{name || bot.name}</div>
        <div className="text-xs text-muted-foreground mono">@{bot.username || "username"}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">bot</div>
      </div>
      <div className="px-4 py-3 border-t border-border">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">About</div>
        <div className="text-sm text-foreground whitespace-pre-wrap break-words" data-testid="preview-profile-about">
          {short || <span className="text-muted-foreground italic">Short description preview (0/120)</span>}
        </div>
      </div>
      <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground">
        The picture above is the profile photo (avatar). The intro picture appears on the empty-chat screen — switch to “Intro / Empty chat”.
      </div>
    </div>
  );
}

function IntroPreview({ bot, name, desc, intro, loading }) {
  const media = intro?.media;
  const showMedia = loading || !media || media.kind !== "none";
  return (
    <div className="rounded-xl border border-border bg-muted/40 max-w-sm shadow-sm overflow-hidden flex flex-col min-h-[26rem]" data-testid="preview-intro">
      <div className="flex items-center gap-2.5 px-3 py-2 bg-card border-b border-border">
        <div className="rounded-full overflow-hidden"><BotAvatar name={name || bot.name} username={bot.username} size={32} /></div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate text-foreground">{name || bot.name}</div>
          <div className="text-[11px] text-muted-foreground">bot</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-[270px] rounded-2xl bg-card border border-border shadow-sm overflow-hidden">
          {showMedia ? (
            <div className="bg-muted flex items-center justify-center" data-testid="preview-intro-media">
              <IntroMediaView bot={bot} intro={intro} loading={loading} compact />
            </div>
          ) : null}
          <div className="p-3 text-center">
            <div className="text-sm font-semibold text-foreground mb-1">What can this bot do?</div>
            <div className="text-[13px] text-foreground/90 whitespace-pre-wrap break-words" data-testid="preview-intro-description">
              {desc || <span className="text-muted-foreground italic">The description appears here on the empty-chat screen before a user presses START (0/512).</span>}
            </div>
          </div>
        </div>
      </div>
      <div className="p-2 bg-card border-t border-border">
        <div className="w-full text-center py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold tracking-wide" data-testid="preview-intro-start">START</div>
      </div>
    </div>
  );
}

/* ---------------- PROFILE ---------------- */
function Profile({ bot }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [intro, setIntro] = useState(null);
  const [introLoading, setIntroLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [previewMode, setPreviewMode] = useState("profile");

  const load = useCallback(() => {
    setErr(null);
    api.get(`/bots/${bot.id}/profile`)
      .then((r) => setP({ name: r.data.name || "", description: r.data.description || "", short_description: r.data.short_description || "" }))
      .catch((e) => setErr(apiErr(e)));
  }, [bot.id]);

  const loadIntro = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setIntroLoading(true);
    try {
      const { data } = await api.get(`/bots/${bot.id}/intro`, { params: refresh ? { refresh: 1 } : {} });
      setIntro(data);
      if (refresh) {
        toast.success("Intro refreshed from Telegram", {
          description: data.media ? (data.media.kind === "none" ? "No description media is set." : `Description media: ${data.media.kind}.`) : (data.reason || "Media state unknown."),
        });
      }
    } catch (e) {
      const msg = apiErr(e);
      setIntro((cur) => cur || { media: null, reason: msg, media_supported: { read: true, write: false } });
      if (refresh) toast.error(msg);
    }
    setIntroLoading(false);
    setRefreshing(false);
  }, [bot.id]);

  useEffect(() => { load(); loadIntro(); }, [load, loadIntro]);

  const save = async (fields, label) => {
    setBusy(true);
    try { await api.put(`/bots/${bot.id}/profile`, fields); toast.success(label, { description: "Confirmed by Telegram." }); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  if (err) return <ErrorPanel msg={err} onRetry={load} testId="profile-error" />;
  if (!p) return <Skeleton className="h-64 w-full" />;

  const shortLen = p.short_description.length;
  const descLen = p.description.length;

  return (
    <div className="grid lg:grid-cols-2 gap-5 items-start">
      <div className="space-y-5">
        <Card className="space-y-4">
          <div className="flex items-center gap-2"><h3 className="font-semibold">Profile</h3><CapabilityChip chip="Direct" /></div>
          <div>
            <Label>Display name</Label>
            <Input data-testid="input-profile-name" maxLength={64} value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
          </div>
          <Button onClick={() => save({ name: p.name }, "Display name saved")} disabled={busy || !p.name.trim()} data-testid="btn-save-profile" className="gap-1.5">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} Save name
          </Button>
          <ProfilePhoto bot={bot} name={p.name} />
        </Card>

        <Card className="space-y-4" data-testid="card-description-intro">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-xs uppercase tracking-wide">Description &amp; Intro</h3>
            <CapabilityChip chip="Direct" />
            <span className="text-[11px] text-muted-foreground">what users see on the profile and on the empty-chat screen</span>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>About / Short description</Label>
              <span className={`text-[11px] mono ${shortLen > 120 ? "text-rose-500" : "text-muted-foreground"}`} data-testid="counter-short">{shortLen}/120</span>
            </div>
            <Textarea data-testid="input-profile-short" maxLength={120} rows={2} value={p.short_description}
                      onChange={(e) => setP({ ...p, short_description: e.target.value })}
                      placeholder="Shown on the bot's profile page and in shared links." />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Description / What can this bot do?</Label>
              <span className={`text-[11px] mono ${descLen > 512 ? "text-rose-500" : "text-muted-foreground"}`} data-testid="counter-desc">{descLen}/512</span>
            </div>
            <Textarea data-testid="input-profile-desc" maxLength={512} rows={4} value={p.description}
                      onChange={(e) => setP({ ...p, description: e.target.value })}
                      placeholder="Shown on the empty-chat screen before the user presses START." />
          </div>
          <Button onClick={() => save({ short_description: p.short_description, description: p.description }, "Description saved")} disabled={busy}
                  data-testid="btn-save-description" className="gap-1.5">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} Save description
          </Button>
          <IntroMedia bot={bot} intro={intro} loading={introLoading} refreshing={refreshing} onRefresh={() => loadIntro(true)} />
        </Card>
      </div>

      <Card className="lg:sticky lg:top-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h3 className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">Live preview</h3>
          <Tabs value={previewMode} onValueChange={setPreviewMode}>
            <TabsList className="h-8" data-testid="preview-mode">
              <TabsTrigger value="profile" className="text-xs h-6 px-2.5" data-testid="preview-mode-profile">Profile</TabsTrigger>
              <TabsTrigger value="intro" className="text-xs h-6 px-2.5" data-testid="preview-mode-intro">Intro / Empty chat</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {previewMode === "profile"
          ? <ProfilePreview bot={bot} name={p.name} short={p.short_description} />
          : <IntroPreview bot={bot} name={p.name} desc={p.description} intro={intro} loading={introLoading} />}
        <p className="text-[11px] text-muted-foreground mt-3">
          {previewMode === "profile"
            ? "Profile: avatar (profile photo), name, username and the About text."
            : "Empty chat: description media (from Telegram via MTProto) above the description text, then START."}
        </p>
      </Card>
    </div>
  );
}

/* ---------------- COMMANDS ---------------- */
function Commands({ bot }) {
  const [cmds, setCmds] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setErr(null); api.get(`/bots/${bot.id}/commands`).then((r) => setCmds(r.data.commands.length ? r.data.commands : [{ command: "start", description: "Start the bot" }])).catch((e) => setErr(apiErr(e))); }, [bot.id]);
  useEffect(() => { load(); }, [load]);
  const update = (i, key, val) => setCmds((c) => c.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const move = (i, dir) => setCmds((c) => { const n = [...c]; const j = i + dir; if (j < 0 || j >= n.length) return c; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/bots/${bot.id}/commands`, { commands: cmds }); toast.success(`Saved ${data.count} commands`, { description: "Confirmed by Telegram." }); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  if (err) return <ErrorPanel msg={err} onRetry={load} testId="commands-error" />;
  if (!cmds) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="space-y-3">
        <div className="flex items-center justify-between"><div className="flex items-center gap-2"><h3 className="font-semibold">Command menu</h3><CapabilityChip chip="Direct" /></div>
          <Button size="sm" variant="outline" onClick={() => setCmds([...cmds, { command: "", description: "" }])} data-testid="btn-add-command" className="gap-1"><Plus size={14} /> Add</Button></div>
        <div className="space-y-2">
          {cmds.map((c, i) => (
            <div key={i} className="flex items-center gap-2 bg-secondary/50 p-2 rounded border border-border">
              <div className="flex flex-col"><button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground text-[9px]">▲</button><button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground text-[9px]">▼</button></div>
              <span className="mono text-primary text-sm">/</span>
              <Input data-testid={`input-command-name-${i}`} className="w-32 mono h-8" value={c.command} onChange={(e) => update(i, "command", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} placeholder="start" />
              <Input data-testid={`input-command-desc-${i}`} className="flex-1 h-8" value={c.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Description" />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => setCmds(cmds.filter((_, idx) => idx !== i))} data-testid={`btn-del-command-${i}`}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={busy} data-testid="btn-save-commands" className="gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Save commands</Button>
      </Card>
      <Card>
        <h3 className="font-semibold mb-3 text-muted-foreground text-xs uppercase tracking-wide">Telegram menu preview</h3>
        <div className="rounded-xl p-3 max-w-sm border border-slate-700" style={{ background: "#0E1621" }}>
          {cmds.filter((c) => c.command).map((c, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-700/40 last:border-0">
              <span className="mono text-sky-400 text-sm">/{c.command}</span><span className="text-slate-300 text-xs">{c.description}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------------- SETTINGS ---------------- */
function SettingsTab({ bot }) {
  const [type, setType] = useState("commands");
  const [text, setText] = useState("Open App");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/bots/${bot.id}/settings/menu-button`, { type, text, url }); toast.success("Menu button updated", { description: "Confirmed by Telegram." }); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const bfItems = [
    ["Group privacy mode", "BotFather"], ["Allow adding to groups", "BotFather"], ["Inline mode", "BotFather"],
    ["Web login domain", "BotFather"], ["Main Mini App", "BotFather"], ["Payments / Stars", "Confirmation"],
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="space-y-4">
        <div className="flex items-center gap-2"><h3 className="font-semibold">Menu button</h3><CapabilityChip chip="Direct" /></div>
        <Select value={type} onValueChange={setType}><SelectTrigger data-testid="select-menu-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="commands">Show commands</SelectItem><SelectItem value="web_app">Open Web App</SelectItem><SelectItem value="default">Default</SelectItem></SelectContent></Select>
        {type === "web_app" && (<>
          <div><Label>Button text</Label><Input data-testid="input-menu-text" value={text} onChange={(e) => setText(e.target.value)} /></div>
          <div><Label>Web App HTTPS URL</Label><Input data-testid="input-menu-url" className="mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://app.example.com" /></div>
        </>)}
        <Button onClick={save} disabled={busy} data-testid="btn-save-menu" className="gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Save menu button</Button>
      </Card>
      <Card>
        <div className="flex items-center gap-2 mb-3"><h3 className="font-semibold">Telegram-gated settings</h3></div>
        <p className="text-xs text-muted-foreground mb-3">These are exposed accurately but must be completed inside Telegram / @BotFather.</p>
        <div className="space-y-2">
          {bfItems.map(([n, chip]) => (
            <div key={n} className="flex items-center justify-between py-2 border-b border-border/50 text-sm"><span>{n}</span><CapabilityChip chip={chip} /></div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------------- MESSAGES ---------------- */
function Messages({ bot }) {
  const [chatId, setChatId] = useState("");
  const [text, setText] = useState("Hello from BotControl 👋");
  const [buttons, setButtons] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    let reply_markup = null;
    const valid = buttons.filter((b) => b.text && b.url);
    if (valid.length) reply_markup = { inline_keyboard: [valid.map((b) => ({ text: b.text, url: b.url }))] };
    try { await api.post(`/bots/${bot.id}/send`, { chat_id: chatId, text, reply_markup }); toast.success("Message sent"); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="space-y-4">
        <div className="flex items-center gap-2"><h3 className="font-semibold">Composer</h3><CapabilityChip chip="Direct" /></div>
        <div><Label>Chat ID</Label><Input data-testid="input-chat-id" className="mono" value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="Numeric chat id (user must have started the bot)" /></div>
        <div><Label>Message</Label><Textarea data-testid="input-message-text" rows={4} value={text} onChange={(e) => setText(e.target.value)} /></div>
        <div>
          <div className="flex items-center justify-between mb-1.5"><Label>Inline URL buttons</Label>
            <Button size="sm" variant="outline" onClick={() => setButtons([...buttons, { text: "", url: "" }])} data-testid="btn-add-button" className="gap-1"><Plus size={13} /> Button</Button></div>
          {buttons.map((b, i) => (
            <div key={i} className="flex gap-2 mb-1.5">
              <Input className="h-8" placeholder="Label" value={b.text} data-testid={`input-btn-text-${i}`} onChange={(e) => setButtons(buttons.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x))} />
              <Input className="h-8 mono" placeholder="https://" value={b.url} data-testid={`input-btn-url-${i}`} onChange={(e) => setButtons(buttons.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => setButtons(buttons.filter((_, idx) => idx !== i))}><Trash2 size={13} /></Button>
            </div>
          ))}
        </div>
        <Button onClick={send} disabled={busy || !chatId || !text} data-testid="btn-send-message" className="gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send message</Button>
      </Card>
      <Card>
        <h3 className="font-semibold mb-3 text-muted-foreground text-xs uppercase tracking-wide">Preview</h3>
        <div className="rounded-xl p-4 max-w-sm border border-slate-700 shadow-xl" style={{ background: "#0E1621" }}>
          <div className="rounded-lg p-3 text-sm text-slate-100 border border-slate-700/50 whitespace-pre-wrap" style={{ background: "#182533" }}>
            {text || "Message preview"}
            {buttons.filter((b) => b.text).length > 0 && (
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {buttons.filter((b) => b.text).map((b, i) => (<div key={i} className="bg-[#2b5278] text-sky-100 text-xs text-center py-1.5 rounded">{b.text}</div>))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- USERS ---------------- */
function UsersTab({ bot }) {
  const [users, setUsers] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/users`).then((r) => setUsers(r.data.items)).catch(() => setUsers([])); }, [bot.id]);
  if (!users) return <Skeleton className="h-40 w-full" />;
  if (!users.length) return <Card><EmptyState title="No users observed yet" desc="Users appear here once they interact with the bot (via webhook updates)." testId="empty-users" /></Card>;
  return (
    <Card className="p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="bg-secondary/50 border-b border-border text-xs uppercase text-muted-foreground"><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Telegram ID</th><th className="px-4 py-3 text-left">Lang</th><th className="px-4 py-3 text-left">Interactions</th><th className="px-4 py-3 text-left">Last seen</th></tr></thead>
        <tbody>{users.map((u) => (<tr key={u.id} className="border-b border-border/50"><td className="px-4 py-2.5">{u.name || u.username || "—"}</td><td className="px-4 py-2.5 mono text-xs">{u.telegram_id}</td><td className="px-4 py-2.5">{u.language || "—"}</td><td className="px-4 py-2.5 mono">{u.interactions || 0}</td><td className="px-4 py-2.5 text-xs text-muted-foreground">{new Date(u.last_seen).toLocaleString()}</td></tr>))}</tbody>
      </table>
    </Card>
  );
}

/* ---------------- WEBHOOK ---------------- */
function WebhookTab({ bot }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setErr(null); return api.get(`/bots/${bot.id}/webhook`).then((r) => setInfo(r.data)).catch((e) => setErr(apiErr(e))); }, [bot.id]);
  useEffect(() => { load(); }, [load]);
  const configure = async () => { setBusy(true); try { await api.post(`/bots/${bot.id}/webhook`, {}); toast.success("Webhook registered", { description: "Confirmed by Telegram." }); load(); } catch (e) { toast.error(apiErr(e)); } setBusy(false); };
  const remove = async () => { setBusy(true); try { await api.delete(`/bots/${bot.id}/webhook`); toast.success("Webhook removed"); load(); } catch (e) { toast.error(apiErr(e)); } setBusy(false); };
  if (err) return <ErrorPanel msg={err} onRetry={load} testId="webhook-error" />;
  if (!info) return <Skeleton className="h-52 w-full" />;
  const health = info.health;
  return (
    <Card className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><h3 className="font-semibold">Webhook</h3><CapabilityChip chip="Direct" /></div>
        <StatusChip status={health === "healthy" ? "HEALTHY" : health === "warning" ? "DEGRADED" : "ACTION_REQUIRED"} pulse />
      </div>
      <Row k="URL" v={<span className="truncate max-w-[280px] inline-block">{info.info.url || "not set"}</span>} />
      <Row k="Pending updates" v={info.info.pending_update_count ?? 0} />
      <Row k="Max connections" v={info.info.max_connections ?? "—"} />
      <Row k="IP address" v={info.info.ip_address || "—"} />
      <Row k="Secret configured" v={info.secret_configured ? "Yes" : "No"} />
      {info.info.last_error_message && <div className="text-xs text-rose-500 flex items-center gap-1.5"><AlertCircle size={13} /> {info.info.last_error_message}</div>}
      <div className="flex gap-2">
        <Button onClick={configure} disabled={busy} data-testid="btn-configure-webhook" className="gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {info.info.url ? "Re-register" : "Register webhook"}</Button>
        {info.info.url && <Button variant="outline" onClick={remove} disabled={busy} data-testid="btn-remove-webhook">Remove</Button>}
      </div>
      <p className="text-xs text-muted-foreground">Registers a managed HTTPS endpoint with a rotating secret token. Updates flow into Users, Chats &amp; Analytics.</p>
    </Card>
  );
}

/* ---------------- AUTOMATIONS (per bot) ---------------- */
function AutomationsTab({ bot }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trigger: "command_start", trigger_value: "", action: "send_message", action_config: { text: "" }, enabled: true });
  const load = useCallback(() => api.get(`/automations`, { params: { bot_id: bot.id } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), [bot.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post(`/bots/${bot.id}/automations`, form); toast.success("Automation created"); setOpen(false); load(); } catch (e) { toast.error(apiErr(e)); } };
  if (!items) return <Skeleton className="h-40 w-full" />;
  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between"><h3 className="font-semibold">Automations</h3>
        <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button size="sm" className="gap-1" data-testid="btn-new-automation"><Plus size={14} /> New</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>New automation</DialogTitle><DialogDescription>Event → action, executed by background workers.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="input-automation-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>When (trigger)</Label><Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}><SelectTrigger data-testid="select-trigger"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="command_start">/start received</SelectItem><SelectItem value="member_join">New member joins</SelectItem><SelectItem value="callback">Callback clicked</SelectItem><SelectItem value="payment_success">Payment succeeds</SelectItem></SelectContent></Select></div>
              <div><Label>Then send message</Label><Textarea data-testid="input-automation-text" value={form.action_config.text} onChange={(e) => setForm({ ...form, action_config: { text: e.target.value } })} /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.name} data-testid="btn-save-automation">Create</Button></DialogFooter>
          </DialogContent></Dialog>
      </div>
      {items.length === 0 ? <EmptyState title="No automations" desc="Create event→action rules that run in the background." testId="empty-automations" /> :
        <div className="space-y-2">{items.map((a) => (
          <div key={a.id} className="flex items-center justify-between p-3 bg-secondary/40 rounded border border-border">
            <div><div className="font-medium text-sm">{a.name}</div><div className="text-xs text-muted-foreground mono">WHEN {a.trigger} → {a.action}</div></div>
            <div className="flex items-center gap-2"><span className="text-xs mono">{a.runs} runs</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" data-testid={`btn-del-automation-${a.id}`} onClick={async () => { await api.delete(`/automations/${a.id}`); load(); }}><Trash2 size={13} /></Button></div>
          </div>))}</div>}
    </Card>
  );
}

/* ---------------- ANALYTICS ---------------- */
function AnalyticsTab({ bot }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/analytics`).then((r) => setA(r.data)).catch(() => {}); }, [bot.id]);
  if (!a) return <Skeleton className="h-64 w-full" />;
  const t = a.totals;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Incoming", t.incoming], ["Outgoing", t.outgoing], ["Users", t.users], ["Errors", t.errors], ["API calls", t.api_calls], ["Delivery rate", `${t.delivery_rate}%`], ["Avg latency", `${t.avg_latency_ms}ms`]].map(([l, v]) => (
          <div key={l} className="bg-card border border-border rounded-lg p-4"><div className="text-xs uppercase text-muted-foreground">{l}</div><div className="text-2xl font-bold mono mt-1">{v}</div></div>
        ))}
      </div>
      <Card><h3 className="font-semibold mb-3">Incoming vs outgoing</h3>
        <ResponsiveContainer width="100%" height={240}><AreaChart data={a.series}><CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" /><XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(d) => d.slice(5)} /><YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} /><Area type="monotone" dataKey="incoming" stroke="#229ED9" fill="#229ED9" fillOpacity={0.15} strokeWidth={2} /><Area type="monotone" dataKey="outgoing" stroke="#10b981" fill="transparent" strokeWidth={2} /></AreaChart></ResponsiveContainer>
      </Card>
    </div>
  );
}

/* ---------------- LOGS ---------------- */
function LogsTab({ bot }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/logs`).then((r) => setLogs(r.data.items)).catch(() => setLogs([])); }, [bot.id]);
  if (!logs) return <Skeleton className="h-40 w-full" />;
  if (!logs.length) return <Card><EmptyState title="No logs yet" desc="Telegram API calls for this bot will appear here." testId="empty-logs" /></Card>;
  return (
    <Card className="p-0 overflow-hidden"><div className="divide-y divide-border/50 max-h-[520px] overflow-y-auto mono text-xs">
      {logs.map((l) => (<div key={l.id} className="px-4 py-2 flex items-center gap-3">
        <span className={l.ok ? "text-emerald-500" : "text-rose-500"}>{l.ok ? "OK " : "ERR"}</span>
        <span className="text-primary w-40 truncate">{l.method}</span>
        <span className="text-muted-foreground">{l.duration_ms}ms</span>
        <span className="text-muted-foreground flex-1 truncate">{l.error || ""}</span>
        <span className="text-muted-foreground">{new Date(l.timestamp).toLocaleTimeString()}</span>
      </div>))}
    </div></Card>
  );
}

/* ---------------- SECURITY ---------------- */
function SecurityTab({ bot }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/security`).then((r) => setS(r.data)).catch(() => {}); }, [bot.id]);
  if (!s) return <Skeleton className="h-52 w-full" />;
  const color = s.score >= 80 ? "text-emerald-500" : s.score >= 50 ? "text-amber-500" : "text-rose-500";
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="flex flex-col items-center justify-center">
        <div className="text-xs uppercase text-muted-foreground mb-2">Security score</div>
        <div className={`text-5xl font-bold mono ${color}`}>{s.score}</div>
        <div className="text-xs text-muted-foreground mt-2">out of 100</div>
      </Card>
      <Card className="lg:col-span-2"><h3 className="font-semibold mb-3">Checks</h3>
        <div className="space-y-2">{s.checks.map((c, i) => (
          <div key={i} className="flex items-start gap-2 text-sm py-1.5 border-b border-border/50">
            {c.status === "pass" ? <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" /> : <AlertCircle size={16} className="text-amber-500 mt-0.5" />}
            <div><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.detail}</div></div>
          </div>))}</div>
      </Card>
    </div>
  );
}

/* ---------------- MANAGED BOT ACCESS SETTINGS (Bot API 10.0) ---------------- */
function AccessTab({ bot }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newId, setNewId] = useState("");

  const load = useCallback(() => {
    setErr(null);
    return api.get(`/bots/${bot.id}/access-settings`)
      .then((r) => setState({
        is_access_restricted: !!r.data.is_access_restricted,
        added_users: r.data.added_users || [],
        added_user_ids: (r.data.added_users || []).map((u) => u.id),
        max: r.data.max_added_users ?? 10,
      }))
      .catch((e) => setErr(apiErr(e)));
  }, [bot.id]);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setBusy(true);
    try {
      await api.put(`/bots/${bot.id}/access-settings`, {
        is_access_restricted: next.is_access_restricted,
        added_user_ids: next.added_user_ids,
      });
      toast.success("Access settings saved", { description: "Confirmed by Telegram." });
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  if (err) return <ErrorPanel msg={err} onRetry={load} testId="access-error" />;
  if (!state) return <Skeleton className="h-52 w-full" />;

  const addUser = () => {
    const id = parseInt(newId.trim(), 10);
    if (!Number.isFinite(id)) { toast.error("Enter a numeric Telegram user id."); return; }
    if (state.added_user_ids.includes(id)) { toast.error("That user is already on the list."); return; }
    if (state.added_user_ids.length >= state.max) { toast.error(`Telegram allows at most ${state.max} extra users.`); return; }
    setNewId("");
    save({ ...state, added_user_ids: [...state.added_user_ids, id] });
  };

  return (
    <Card className="space-y-4 max-w-2xl">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">Access settings</h3>
        <CapabilityChip chip="Managed" />
        <span className="text-[11px] text-muted-foreground mono">get/setManagedBotAccessSettings</span>
      </div>
      <p className="text-sm text-muted-foreground">
        Restrict who can talk to this managed bot. The owner always retains access; you may allow up to {state.max} additional users.
      </p>
      <div className="flex items-center justify-between py-2 border-y border-border">
        <div>
          <div className="text-sm font-medium">Restrict access</div>
          <div className="text-xs text-muted-foreground">{state.is_access_restricted ? "Only the owner and the users below can use the bot." : "Anyone can use the bot."}</div>
        </div>
        <Switch
          checked={state.is_access_restricted}
          disabled={busy}
          onCheckedChange={(v) => save({ ...state, is_access_restricted: v })}
          data-testid="switch-access-restricted"
        />
      </div>
      <div className={state.is_access_restricted ? "" : "opacity-50 pointer-events-none"}>
        <Label>Allowed users (Telegram user ids)</Label>
        <div className="flex gap-2 mt-1.5">
          <Input className="mono" placeholder="123456789" value={newId} onChange={(e) => setNewId(e.target.value.replace(/[^0-9]/g, ""))} data-testid="input-access-user-id" />
          <Button onClick={addUser} disabled={busy || !newId} data-testid="btn-add-access-user" className="gap-1.5"><Plus size={14} /> Add</Button>
        </div>
        <div className="mt-3 space-y-2" data-testid="access-user-list">
          {state.added_user_ids.length === 0 ? (
            <div className="text-xs text-muted-foreground">No additional users — owner only.</div>
          ) : state.added_user_ids.map((id) => {
            const u = state.added_users.find((x) => x.id === id);
            return (
              <div key={id} className="flex items-center justify-between p-2.5 bg-secondary/40 rounded border border-border">
                <div>
                  <div className="text-sm font-medium">{u ? [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username || id : id}</div>
                  <div className="text-xs text-muted-foreground mono">{u?.username ? `@${u.username} · ` : ""}{id}</div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" disabled={busy}
                  data-testid={`btn-remove-access-user-${id}`}
                  onClick={() => save({ ...state, added_user_ids: state.added_user_ids.filter((x) => x !== id) })}>
                  <Trash2 size={13} />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ---------------- CAPABILITIES ---------------- */
function CapabilitiesTab({ bot }) {
  const [caps, setCaps] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/capabilities`).then((r) => setCaps(r.data.capabilities)).catch(() => {}); }, [bot.id]);
  if (!caps) return <Skeleton className="h-64 w-full" />;
  return (
    <Card className="p-0 overflow-hidden"><table className="w-full text-sm">
      <thead><tr className="bg-secondary/50 border-b border-border text-xs uppercase text-muted-foreground"><th className="px-4 py-3 text-left">Capability</th><th className="px-4 py-3 text-left">Category</th><th className="px-4 py-3 text-left">Method</th><th className="px-4 py-3 text-left">Support</th></tr></thead>
      <tbody>{caps.map((c) => (<tr key={c.name} className="border-b border-border/50"><td className="px-4 py-2.5 font-medium">{c.name}</td><td className="px-4 py-2.5 text-muted-foreground">{c.category}</td><td className="px-4 py-2.5 mono text-xs text-primary">{c.telegramMethod || "—"}</td><td className="px-4 py-2.5"><CapabilityChip chip={c.chip} /></td></tr>))}</tbody>
    </table></Card>
  );
}

/* ---------------- HOSTED (BotNexus) ---------------- */
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Enqueue a lifecycle action, then poll the bot until its state reaches the
// expected target (or the attempts run out) so the UI reflects reality without
// forcing the operator to refresh. The action itself runs as a job on BotControl.
function HostedActions({ bot, onDone }) {
  const [busy, setBusy] = useState(null);
  const run = async (action) => {
    setBusy(action);
    try {
      await api.post(`/federation/bots/${bot.id}/action`, { action });
      toast.success(`${cap(action)} requested`, { description: "Running on BotNexus…" });
      const target = action === "stop" ? "stopped" : "running";
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, 1300));
        try {
          const { data } = await api.get(`/bots/${bot.id}`);
          if (data?.external?.state === target || data?.external?.last_action_error) {
            if (data?.external?.last_action_error)
              toast.error(`${cap(action)} rejected`, { description: data.external.last_action_error });
            break;
          }
        } catch { /* keep polling */ }
      }
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(null);
  };
  const running = bot.external?.state === "running";
  const spin = (a) => (busy === a ? <Loader2 size={14} className="animate-spin" /> : null);
  return (
    <div className="flex items-center gap-2" data-testid="hosted-actions">
      <Button size="sm" variant="outline" className="gap-1.5" disabled={!!busy || running}
        onClick={() => run("start")} data-testid="btn-hosted-start">
        {spin("start") || <Play size={14} />} Start
      </Button>
      <Button size="sm" variant="outline" className="gap-1.5" disabled={!!busy}
        onClick={() => run("restart")} data-testid="btn-hosted-restart">
        {spin("restart") || <RotateCw size={14} />} Restart
      </Button>
      <Button size="sm" variant="outline" className="gap-1.5 text-rose-500" disabled={!!busy || !running}
        onClick={() => run("stop")} data-testid="btn-hosted-stop">
        {spin("stop") || <Square size={14} />} Stop
      </Button>
    </div>
  );
}

function HostedOverview({ bot }) {
  const e = bot.external || {};
  const fmtUptime = (s) => {
    if (!s || s <= 0) return "—";
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card>
        <div className="flex items-center gap-2 mb-3"><Server size={16} className="text-primary" /><h3 className="font-semibold">Hosted process</h3></div>
        <Row k="Execution backend" v={bot.execution_backend_name || "—"} />
        <Row k="State" v={cap(e.state) || "—"} />
        <Row k="Process" v={e.process_alive ? `alive (pid ${e.pid ?? "?"})` : "not running"} />
        <Row k="Uptime" v={fmtUptime(e.uptime_seconds)} />
        <Row k="CPU" v={e.cpu_percent != null ? `${e.cpu_percent}%` : "—"} />
        <Row k="Memory" v={e.memory_mb != null ? `${e.memory_mb} MB` : "—"} />
        <Row k="Restarts" v={e.restart_count ?? "—"} />
        <Row k="Activity" v={cap(e.activity_status) || "—"} />
      </Card>
      <Card>
        <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-primary" /><h3 className="font-semibold">Definition</h3></div>
        <Row k="Adapter" v={e.adapter || "—"} />
        <Row k="Framework" v={e.framework || "—"} />
        <Row k="Family" v={e.family || "—"} />
        <Row k="Environment" v={e.environment || "—"} />
        <Row k="Script" v={e.script || "—"} />
        <Row k="Secrets set" v={(e.secrets_configured || []).join(", ") || "—"} />
        <div className="mt-3 space-y-1.5">
          {(bot.health?.reasons || []).map((r, i) => (
            <div key={i} className="text-xs flex items-start gap-1.5 text-muted-foreground">
              {bot.health?.status === "HEALTHY" ? <CheckCircle2 size={13} className="text-emerald-500 mt-0.5" /> : <AlertCircle size={13} className="text-amber-500 mt-0.5" />} {r}
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Read-only federation: BotControl never stores this bot's token, config or session — only non-secret status.
        </p>
      </Card>
    </div>
  );
}

function HostedLogs({ bot }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/federation/bots/${bot.id}/logs`, { params: { tail: 400 } });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  }, [bot.id]);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh polls the live tail while the toggle is on.
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [auto, load]);
  return (
    <Card>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">Process logs</h3>
          <span className="text-[11px] text-muted-foreground mono">{bot.execution_backend_name || "BotNexus"} · tail 400{data?.count ? ` · ${data.count} lines` : ""}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <Switch checked={auto} onCheckedChange={setAuto} data-testid="switch-logs-auto" /> Auto-refresh
          </label>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={load} disabled={busy} data-testid="btn-logs-refresh">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </Button>
        </div>
      </div>
      {!data ? (
        <Skeleton className="h-72 w-full" />
      ) : data.lines?.length ? (
        <pre className="text-[11px] leading-relaxed mono bg-black/50 border border-border rounded p-3 max-h-[30rem] overflow-auto whitespace-pre-wrap" data-testid="hosted-logs">
          {data.lines.join("\n")}
        </pre>
      ) : (
        <div className="text-sm text-muted-foreground py-8 text-center">{data.message || "No captured logs yet."}</div>
      )}
    </Card>
  );
}

/* ---------------- MAIN ---------------- */
export default function BotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bot, setBot] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [rotateOpen, setRotateOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(() => api.get(`/bots/${id}`).then((r) => setBot(r.data)).catch((e) => { toast.error(apiErr(e)); navigate("/bots"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!bot) return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-96 w-full" /></div>;

  const isHosted = bot.runtime === "botnexus";
  const tabProps = { bot };
  const RENDER = {
    Overview: isHosted ? HostedOverview : Overview,
    "Welcome Flow": WelcomeFlowTab,
    Profile, Commands, Settings: SettingsTab, Messages, Users: UsersTab, Webhook: WebhookTab,
    Automations: AutomationsTab, Analytics: AnalyticsTab, Logs: isHosted ? HostedLogs : LogsTab,
    Security: SecurityTab, Access: AccessTab, Capabilities: CapabilitiesTab,
  };
  // A hosted bot is a remote process, not a Telegram-managed bot — the profile/
  // commands/webhook tabs would all hit endpoints that don't apply to it. It does
  // get its own process Logs, fetched live from BotNexus.
  const TABS = isHosted ? ["Overview", "Logs"] : tabsFor(bot);
  const Active = RENDER[tab] || RENDER.Overview;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/bots")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="btn-back-bots"><ChevronLeft size={16} /> Bots</button>

      <div className="bg-card border border-border rounded-lg p-5 flex flex-wrap items-center gap-4">
        <BotAvatar name={bot.name} username={bot.username} size={56} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><h1 className="text-xl font-bold truncate">{bot.name}</h1><StatusChip status={bot.status} /><StatusChip status={bot.health?.status || "OFFLINE"} pulse /></div>
          <div className="mono text-sm text-primary">{isHosted ? `${bot.external?.adapter || bot.type || "hosted"} · ${bot.execution_backend_name || "BotNexus"}` : `@${bot.username || "unassigned"}`}</div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {isHosted ? (
            <HostedActions bot={bot} onDone={load} />
          ) : (
            <>
              {bot.username && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open(`https://t.me/${bot.username}`, "_blank")} data-testid="btn-open-telegram"><ExternalLink size={14} /> Open Telegram</Button>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="icon" data-testid="btn-bot-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setRotateOpen(true)} data-testid="menu-rotate-token"><KeyRound size={14} className="mr-2" /> Rotate token</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDelOpen(true)} className="text-rose-500" data-testid="menu-delete-bot"><Trash2 size={14} className="mr-2" /> Remove bot</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="bot-detail-tabs">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`tab-${t.toLowerCase()}`}>{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={tab} className="mt-5 animate-fade-in"><Active {...tabProps} /></TabsContent>
      </Tabs>

      <RotateDialog bot={bot} open={rotateOpen} onOpenChange={setRotateOpen} onDone={load} />
      <DeleteDialog bot={bot} open={delOpen} onOpenChange={setDelOpen} onDone={() => navigate("/bots")} />
    </div>
  );
}

function RotateDialog({ bot, open, onOpenChange, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post(`/bots/${bot.id}/token/rotate`, { confirm_username: confirm, new_token: token || undefined }); toast.success("Token rotated", { description: "Old token revoked." }); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent>
      <DialogHeader><DialogTitle>Rotate token</DialogTitle><DialogDescription>Dangerous operation. Type <span className="mono font-semibold">{bot.username}</span> to confirm.</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <Input data-testid="input-rotate-confirm" placeholder={bot.username} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <div><Label>New token (paste from BotFather, or leave blank if a Manager Bot is configured)</Label><Input data-testid="input-rotate-token" className="mono" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456:ABC…" /></div>
      </div>
      <DialogFooter><Button variant="destructive" onClick={run} disabled={busy || confirm !== bot.username} data-testid="btn-confirm-rotate">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Rotate</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function DeleteDialog({ bot, open, onOpenChange, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post(`/bots/${bot.id}/delete`, { confirm_username: confirm }); toast.success("Bot removed from manager"); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent>
      <DialogHeader><DialogTitle className="text-rose-500">Remove bot</DialogTitle><DialogDescription>This removes the bot from the manager (the bot itself is deleted in @BotFather). Type <span className="mono font-semibold">{bot.username}</span> to confirm.</DialogDescription></DialogHeader>
      <Input data-testid="input-delete-confirm" placeholder={bot.username} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      <DialogFooter><Button variant="destructive" onClick={run} disabled={busy || confirm !== bot.username} data-testid="btn-confirm-delete">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Remove</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
